import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

type Line = { id: number; code: string; name: string };
type Promotion = {
  id: number;
  lineId: number;
  lineCode: string;
  productId: number | null;
  partNumber: string | null;
  label: string;
  kind: 'PERCENT' | 'AMOUNT' | 'TIER';
  value: string;
  minQty: number;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
};

const GENRES = [
  { value: 'PERCENT', label: 'Pourcentage', aide: 'ex. 10 = 10 %', unite: '%' },
  { value: 'AMOUNT', label: 'Montant / unité', aide: 'ex. 3.00 = 3 $ l\'unité', unite: '$' },
  { value: 'TIER', label: 'Palier', aide: 'rabais $/unité au-delà du seuil', unite: '$' },
] as const;

function decrire(p: Promotion): string {
  const cible = p.partNumber ? `produit ${p.partNumber}` : `toute la ligne ${p.lineCode}`;
  if (p.kind === 'PERCENT') return `${p.value} % sur ${cible}`;
  if (p.kind === 'AMOUNT') return `${p.value} $ l'unité sur ${cible}`;
  return `${p.value} $ l'unité dès ${p.minQty} sur ${cible}`;
}

export default function Promotions() {
  const qc = useQueryClient();
  const invalider = () => qc.invalidateQueries({ queryKey: ['admin', 'promotions'] });

  const promos = useQuery<{ promotions: Promotion[] }>({
    queryKey: ['admin', 'promotions'],
    queryFn: () => api('/api/admin/promotions'),
  });
  const lignes = useQuery<{ lines: Line[] }>({
    queryKey: ['admin', 'lines'],
    queryFn: () => api('/api/admin/lines'),
  });

  const supprimer = useMutation({
    mutationFn: (id: number) => api(`/api/admin/promotions/${id}`, { method: 'DELETE' }),
    onSuccess: invalider,
  });
  const basculer = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      api(`/api/admin/promotions/${id}`, { method: 'PATCH', body: JSON.stringify({ isActive }) }),
    onSuccess: invalider,
  });

  return (
    <div className="space-y-6">
      <FormulairePromo lignes={lignes.data?.lines ?? []} onCree={invalider} />

      <section className="bg-card rounded-xl border p-6">
        <h2 className="mb-4 text-lg font-semibold">Promotions</h2>

        {promos.data?.promotions.length === 0 && (
          <p className="text-muted-foreground text-sm">Aucune promotion pour l'instant.</p>
        )}

        <ul className="space-y-2">
          {promos.data?.promotions.map((p) => (
            <li
              key={p.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-xs font-medium">
                    {p.lineCode}
                  </span>
                  <span className="font-medium">{p.label}</span>
                  {!p.isActive && <span className="text-muted-foreground text-xs">désactivée</span>}
                </div>
                <p className="text-muted-foreground mt-0.5 text-sm">{decrire(p)}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => basculer.mutate({ id: p.id, isActive: !p.isActive })}
                  className="border-input rounded-lg border px-3 py-1.5 text-sm"
                >
                  {p.isActive ? 'Désactiver' : 'Réactiver'}
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Supprimer « ${p.label} » ?`)) supprimer.mutate(p.id);
                  }}
                  className="text-destructive border-input rounded-lg border px-3 py-1.5 text-sm"
                >
                  Supprimer
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function FormulairePromo({ lignes, onCree }: { lignes: Line[]; onCree: () => void }) {
  const [form, setForm] = useState({
    lineId: '',
    label: '',
    kind: 'PERCENT' as Promotion['kind'],
    value: '',
    minQty: '1',
  });

  const creer = useMutation({
    mutationFn: () =>
      api('/api/admin/promotions', {
        method: 'POST',
        body: JSON.stringify({
          lineId: Number(form.lineId),
          label: form.label.trim(),
          kind: form.kind,
          value: form.value.trim(),
          minQty: Number(form.minQty),
        }),
      }),
    onSuccess: () => {
      setForm({ lineId: form.lineId, label: '', kind: 'PERCENT', value: '', minQty: '1' });
      onCree();
    },
  });

  const genre = GENRES.find((g) => g.value === form.kind)!;
  const valide = form.lineId && form.label.trim() && form.value.trim();

  return (
    <section className="bg-card rounded-xl border p-6">
      <h2 className="mb-4 text-lg font-semibold">Nouvelle promotion</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valide) creer.mutate();
        }}
        className="space-y-4"
      >
        <div className="flex flex-wrap gap-3">
          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Ligne</span>
            <select
              value={form.lineId}
              onChange={(e) => setForm({ ...form, lineId: e.target.value })}
              required
              className="border-input bg-background h-11 w-48 rounded-lg border px-3"
            >
              <option value="">Choisir…</option>
              {lignes.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.code} — {l.name}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-1.5 flex-1">
            <span className="block text-sm font-medium">Libellé (vu par le rep)</span>
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="5 % sur toute la ligne Valvoline"
              maxLength={191}
              required
              className="border-input bg-background h-11 w-full min-w-56 rounded-lg border px-3"
            />
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Genre</span>
            <select
              value={form.kind}
              onChange={(e) =>
                setForm({
                  ...form,
                  kind: e.target.value as Promotion['kind'],
                  minQty: e.target.value === 'TIER' ? '12' : '1',
                })
              }
              className="border-input bg-background h-11 w-44 rounded-lg border px-3"
            >
              {GENRES.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Valeur ({genre.unite})</span>
            <input
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              inputMode="decimal"
              placeholder={genre.aide}
              required
              className="border-input bg-background h-11 w-40 rounded-lg border px-3"
            />
          </label>

          {form.kind === 'TIER' && (
            <label className="space-y-1.5">
              <span className="block text-sm font-medium">À partir de (unités)</span>
              <input
                value={form.minQty}
                onChange={(e) => setForm({ ...form, minQty: e.target.value })}
                inputMode="numeric"
                className="border-input bg-background h-11 w-32 rounded-lg border px-3"
              />
            </label>
          )}

          <button
            type="submit"
            disabled={creer.isPending || !valide}
            className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-40"
          >
            Ajouter
          </button>
        </div>

        <p className="text-muted-foreground text-xs">{genre.aide}.</p>
        {creer.isError && (
          <p className="text-destructive text-sm font-medium">{(creer.error as Error).message}</p>
        )}
      </form>
    </section>
  );
}
